import React from 'react';
import { Send, Trash2 } from 'lucide-react';
import type { WorkspaceInvite } from '../../../types';

interface Props {
  invite: WorkspaceInvite;
  canManage: boolean;
  busy: boolean;
  onResend: (invite: WorkspaceInvite) => void;
  onRevoke: (invite: WorkspaceInvite) => void;
}

const statusPill: Record<WorkspaceInvite['status'], string> = {
  open: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
  accepted: 'bg-green-500/10 text-green-300 border-green-500/30',
  expired: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  revoked: 'bg-slate-500/10 text-slate-300 border-slate-500/30',
};

const statusLabel: Record<WorkspaceInvite['status'], string> = {
  open: 'Pending',
  accepted: 'Accepted',
  expired: 'Expired',
  revoked: 'Revoked',
};

const InviteRow: React.FC<Props> = ({ invite, canManage, busy, onResend, onRevoke }) => {
  const canResend =
    canManage && (invite.status === 'open' || invite.status === 'expired');
  const canRevoke = canManage && invite.status === 'open';

  return (
    <tr className="hover:bg-white/5 transition-colors">
      <td className="px-3 py-2.5 text-sm text-white truncate">{invite.email}</td>
      <td className="px-3 py-2.5 text-sm capitalize text-gray-300 hidden md:table-cell">
        {invite.role}
      </td>
      <td className="px-3 py-2.5 text-sm">
        <span className={`px-2 py-0.5 text-xs rounded-full border ${statusPill[invite.status]}`}>
          {statusLabel[invite.status]}
        </span>
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-400 hidden lg:table-cell">
        {new Date(invite.expires_at).toLocaleDateString()}
      </td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        {canResend && (
          <button
            type="button"
            onClick={() => onResend(invite)}
            disabled={busy}
            className="p-1.5 text-cyan-300 hover:text-cyan-200 hover:bg-cyan-500/10 rounded disabled:opacity-50"
            aria-label={`Resend invite to ${invite.email}`}
            title="Resend invite"
          >
            <Send className="w-4 h-4" />
          </button>
        )}
        {canRevoke && (
          <button
            type="button"
            onClick={() => onRevoke(invite)}
            disabled={busy}
            className="p-1.5 text-red-300 hover:text-red-200 hover:bg-red-500/10 rounded disabled:opacity-50"
            aria-label={`Revoke invite for ${invite.email}`}
            title="Revoke invite"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );
};

export default InviteRow;
